import { type FC } from "react";

import { colorByPlatform } from "~/feature/chart/constants";
import type { IMarketGroupChanceHistory } from "~/feature/chart/types";
import { EMBED_CHART_EMPTY, EMBED_GOLD } from "~/feature/embed/constants";
import {
  getEmbedSparkline,
  getSparklineAxisLabels,
  type IEmbedSparklineMarket,
  type IEmbedSparklineSeries,
} from "~/feature/embed/utils/getEmbedSparkline";

import * as Styled from "./EmbedSparkline.styles";

interface IEmbedSparklineProps {
  history: IMarketGroupChanceHistory[];
  markets: IEmbedSparklineMarket[];
  isConsensusMode: boolean;
  consensusOnly: boolean;
}

const getColor = ({ isConsensus, platform }: IEmbedSparklineSeries): string =>
  isConsensus || !platform ? EMBED_GOLD : colorByPlatform[platform];

export const EmbedSparkline: FC<IEmbedSparklineProps> = (props) => {
  const { series, gridlines } = getEmbedSparkline(props);

  if (!series.length) {
    return <Styled.Empty>{EMBED_CHART_EMPTY}</Styled.Empty>;
  }

  const consensus = series.find(({ isConsensus }) => isConsensus);
  const labelled = consensus ? [consensus] : series;
  const axisLabels = getSparklineAxisLabels(
    labelled.map((item) => ({
      key: item.id,
      y: item.end.y,
      label: item.end.label,
      color: getColor(item),
    })),
    gridlines
  );

  return (
    <Styled.Root>
      <Styled.Plot>
        <Styled.Svg viewBox="0 0 100 100" preserveAspectRatio="none">
          {gridlines.map(({ y }) => (
            <Styled.Gridline key={y} x1="0" x2="100" y1={y} y2={y} />
          ))}
          {series.map((item) => (
            <Styled.Line
              key={item.id}
              d={item.path}
              stroke={getColor(item)}
              $consensus={item.isConsensus}
              $faded={Boolean(consensus) && !item.isConsensus}
            />
          ))}
        </Styled.Svg>
      </Styled.Plot>

      <Styled.Axis>
        {axisLabels.map(({ key, y, label, color }) => (
          <Styled.AxisLabel
            key={key}
            $value={Boolean(color)}
            style={{ top: `${y}%`, ...(color && { color }) }}
          >
            {label}
          </Styled.AxisLabel>
        ))}
      </Styled.Axis>
    </Styled.Root>
  );
};
